import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { TableModule } from 'primeng/table';
import { AnalyzeData } from './model/analyze-data';
import { map, Observable, switchMap } from 'rxjs';
import { ChartRow } from './model/chart-row';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { SignalsService } from '@shared/services/signals/signals.service';
import { FlightTotalsService } from '../flights/services/flight-totals.service';
import { convertDistance, formatTime } from '@shared/utilities/utilities';
import { ErrorLogService } from '@shared/services/errorhandler/error-log.service';
import { BreadcrumbService } from '@shared/components/breadcrumb/breadcrumb.service';
import { DateTime } from 'luxon';
import { AppService } from 'app/app.service';

@Component({
    selector: 'app-analyze',
    imports: [
        CommonModule,
        TableModule,
        RouterModule,
    ],
    templateUrl: './analyze.component.html',
    styleUrl: './analyze.component.scss'
})
export class AnalyzeComponent implements OnInit {

  empNumber$ = this.signalsService.employeeNumber$;

  analyzeData$?: Observable<AnalyzeData[]>;
  chartRows: ChartRow[] = [];
  selectedRow!: ChartRow;
  distFlown: number = 0;
  units: string = 'mi';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private signalsService: SignalsService,
    private flightTotalsService: FlightTotalsService,
    private errorLogService: ErrorLogService,
    private breadcrumbService: BreadcrumbService,
    public appService: AppService
  ) { }

  ngOnInit(): void {
    this.breadcrumbService.removeBreadcrumb(['Time by Year', 'Time by Seat', 'Type', 'Variant', 'Time by Month']);

    this.analyzeData$ = this.empNumber$.pipe(
      switchMap((empNum) => {
        return this.flightTotalsService.getFlightTimeStats(empNum)
          .pipe(
            map((flightStats) => {
              if (!flightStats || !flightStats.response) {
                this.errorLogService.logError(`[ERROR] - ${DateTime.now().toString()}: Flight Time Stats Error: ${flightStats.status}`);
              }
              let analyzeData: AnalyzeData[] = [
                {
                  label: 'Total Time',
                  value: formatTime(flightStats.response?.totalTime || 0) || '0:00'
                },
                {
                  label: 'Current Year',
                  value: formatTime(flightStats.response?.currentYear || 0) || '0:00'
                },
                {
                  label: 'Current Month',
                  value: formatTime(flightStats.response?.currentMonth || 0) || '0:00'
                },
                {
                  label: 'Last 6 Months',
                  value: formatTime(flightStats.response?.lastSixMonths || 0) || '0:00'
                },
                {
                  label: 'Last 12 Months',
                  value: formatTime(flightStats.response?.lastTwelveMonths || 0) || '0:00'
                },
                {
                  label: 'Distance Flown',
                  value: this.distFlown = Number(flightStats.response?.totalDistance.toFixed(1)) || 0
                },
                {
                  label: 'Passenger Count',
                  value: flightStats.response?.totalPassengerCount || 0
                }
              ];
              return analyzeData;
            })
          );
      })

    );
    this.chartRows = this.getChartRows();
  }

  getChartRows(): ChartRow[] {
    const rows: ChartRow[] = [
      {
        id: 0,
        label: 'Time by Year (Bar Chart)',
        link: 'analyze/bar-chart'
      },
      {
        id: 1,
        label: 'Time by Seat Position (Pie Chart)',
        link: ''
      },
      {
        id: 2,
        label: 'Time by Aircraft (Pie Chart)',
        link: ''
      },
      {
        id: 3,
        label: 'Time by Month (Combo Chart)',
        link: ''
      }
    ];
    return rows;
  }

  onSelectedRow(selectedChart: any): void {
    const url = location.pathname;
    const breadcrumbLabel = this.route.snapshot.data['breadcrumb'] || 'Analyze';
    this.breadcrumbService.addToBreadcrumbs(url, breadcrumbLabel);
    switch (selectedChart.id) {
      case 0:
        this.router.navigate(['analyze/bar-chart']);
        break;
      case 1:
        this.router.navigate(['analyze/pie-chart', selectedChart.id]);
        break;
      case 2:
        this.router.navigate(['analyze/pie-chart', selectedChart.id]);
        break;
      case 3:
        this.router.navigate(['analyze/line-chart']);
        break;
    }
  }

  isNumber(arg0: any) {
    return typeof arg0 === 'number' && !isNaN(arg0);
  }

  onUnitsClick(data: any): void {
    const unitsOrder = ['mi', 'nm', 'km'];
    const currentIndex = unitsOrder.indexOf(this.units);
    this.units = unitsOrder[(currentIndex + 1) % unitsOrder.length];
    const distance = this.getDistance(data.value);
    this.distFlown = 0;
    if (this.units === 'nm') {
      this.distFlown = distance.nauticalMiles;
    } else if (this.units === 'mi') {
      this.distFlown = distance.miles;
    } else {
      this.distFlown = distance.kilometers;
    }
  }

  getDistance(value: number): { 
    nauticalMiles: number, 
    miles: number, 
    kilometers: number 
  } {
    const distances = convertDistance(value);
    return distances;
  }

  dataTableTotals = {
    row: {
      background: 'transparent'
    },
    bodyCell: {
      borderColor: 'transparent'
    }
  }

  dataTableCharts = {
    row: {
      background: 'transparent',
      hoverBackground: '{surface.100}',
      hoverColor: '{surface.900}'
    },
    bodyCell: {
      borderColor: 'transparent'
    },
  }
}
